import "./MobileNavigationBar.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { IconAmwayLogo } from "../../../common/icons/IconAmwayLogo/IconAmwayLogo";
import { IconHamburger } from "../../../common/icons/IconHamburger/IconHamburger";
import { MobileNavigationMenu } from "../MobileNavigationMenu/MobileNavigationMenu";
import type { NavigationApiResponse } from "../../../services/MyBizShell/mybizShellAPI.types";
import { useSectionFromRoute } from "../useSectionFromRoute";
import { ProfileSelectorDropdown } from "../ProfileSelectorDropdown/ProfileSelectorDropdown";
import { useAppSelector } from "../../../store";
import { ROUTE_PATHS } from "../../../common/enums/routing-enums";

interface MobileNavigationBarProps {
  navigationData?: NavigationApiResponse;
  isLoading?: boolean;
  isError?: boolean;
}

export const MobileNavigationBar = ({ navigationData, isLoading, isError }: MobileNavigationBarProps) => {
  useSectionFromRoute();
  const { headless } = useAppSelector((state) => state.loginLocalStorage);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const skipScrollHandler = useRef(false);
  const { pathname } = useLocation();

  const ACTION_REPORT_ROUTES: string[] = [
    ROUTE_PATHS.RENEWALS,
    ROUTE_PATHS.WHOS_NEW_ACTION_REPORT,
    ROUTE_PATHS.WHOS_CLOSE_ACTION_REPORT,
    ROUTE_PATHS.BIRTHDAYS_ACTION_REPORT,
    ROUTE_PATHS.NON_BONUSABLE_BV_ACTION_REPORT,
    ROUTE_PATHS.CVR,
    ROUTE_PATHS.IBO_ONBOARDING,
    ROUTE_PATHS.CBR,
    ROUTE_PATHS.SAQ_ACTION_REPORT,
  ];
  const isActionReport = ACTION_REPORT_ROUTES.includes(pathname);

  // Log error if navigation query fails
  useEffect(() => {
    if (isError) {
      console.error("Failed to load navigation data");
    }
  }, [isError]);

  const handleScroll = useCallback(() => {
    if (skipScrollHandler.current) return;

    const currentScrollY = window.scrollY;
    const scrollingUp = currentScrollY < lastScrollY.current;

    setIsVisible(scrollingUp || currentScrollY <= 0);
    lastScrollY.current = currentScrollY;
  }, [skipScrollHandler]);

  useEffect(() => {
    if (headless || isActionReport) return;

    const suppressScroll = () => {
      skipScrollHandler.current = true;
      setTimeout(() => {
        skipScrollHandler.current = false;
      }, 1500);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("scrollend", suppressScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scrollend", suppressScroll);
    };
  }, [handleScroll, headless, isActionReport]);

  useEffect(() => {
    if (isActionReport) {
      setIsVisible(true);
    }
  }, [isActionReport]);

  if (headless) return null;

  return (
    <>
      <header
        className={`MobileNavigationBar${isVisible ? "" : " MobileNavigationBar--hidden"}`}
        role="banner"
        data-testid="mobile-navigation-bar"
      >
        <Link to="/performance-dashboard">
          <IconAmwayLogo />
        </Link>
        <div className="MobileNavigationBar__right">
          <ProfileSelectorDropdown />
          <button
            type="button"
            className="MobileNavigationBar__menu-button"
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-haspopup="dialog"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(true)}
            disabled={isMenuOpen}
            data-testid="mobile-menu-button"
          >
            <IconHamburger />
          </button>
        </div>
      </header>
      {isMenuOpen && (
        <MobileNavigationMenu
          onClose={() => setIsMenuOpen(false)}
          navigationData={navigationData}
          isLoading={isLoading}
        />
      )}
    </>
  );
};
